import { Button } from "./ui/Button";
import { useAuth } from "context/AuthContext";
import { useEffect, useState } from "react";
import fetchWithCredentials from "apiClient";
import { Plan } from "types/Plan";
import CheckIcon from "./icons/CheckIcon";
import { X, Calendar } from "lucide-react";

interface SubscriptionPlansProps {
  onCancelSubscription?: () => void;
  showCancelButton?: boolean;
}

export function SubscriptionPlans({
  onCancelSubscription,
  showCancelButton,
}: SubscriptionPlansProps) {
  const { currentPlan, planLimit, subscriptionExpiresAt } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [changingPlan, setChangingPlan] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPlans() {
      try {
        const backendPlans: Plan[] = await fetchWithCredentials("/api/plans/");
        setPlans(backendPlans);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
      }
    }

    fetchPlans();
  }, []);

  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handlePlanSelection = async (plan: Plan) => {
    // If user has an active subscription (not Free) and is selecting a different plan
    if (currentPlan !== "Free" && plan.key !== currentPlan) {
      setChangingPlan(plan.key);
      try {
        await fetchWithCredentials("/api/teacher/change-plan/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ plan_name: plan.key }),
        });
        // Refresh the page to update the plan status
        window.location.reload();
      } catch (err) {
        console.error("Failed to change plan:", err);
        setChangingPlan(null);
      }
    } else {
      // For new subscriptions or Free plan, redirect to payment URL
      window.location.href = plan.url;
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 lg:gap-4">
      {plans.map((plan) => (
        <div
          key={plan.key}
          className={`p-4 lg:p-6 rounded-lg border relative ${
            plan.key === currentPlan
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200"
          }`}
        >
          {plan.key.toLowerCase() === "free" && (
            <span
              className={`absolute -top-2 -right-2 text-xs px-2 py-1 rounded-full shadow-sm ${
                planLimit === 0
                  ? "bg-red-100 text-red-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {planLimit === 0 ? "Trial Expired" : "15-Day Free Trial"}
            </span>
          )}
          <h3 className="text-base lg:text-lg font-semibold mb-2">
            {plan.name}
          </h3>
          <p
            className={`text-xl lg:text-2xl font-bold mb-3 lg:mb-4 ${
              plan.key.toLowerCase() === "free" ? "text-gray-400" : ""
            }`}
          >
            ${plan.price}
            <span
              className={`text-xs lg:text-sm font-normal ${
                plan.key.toLowerCase() === "free"
                  ? "text-gray-400"
                  : "text-gray-500"
              }`}
            >
              /month
            </span>
          </p>
          <ul className="space-y-1 lg:space-y-2 mb-4 lg:mb-6">
            <li className="flex items-center text-xs lg:text-sm text-gray-600">
              <CheckIcon />
              {`${plan.limit_1 === 0 ? "No" : plan.limit_1} students`}
            </li>
          </ul>
          <div className="space-y-2">
            <Button
              onClick={() => handlePlanSelection(plan)}
              disabled={
                plan.key === currentPlan ||
                (!plan.url && currentPlan === "Free") ||
                changingPlan === plan.key
              }
              className="w-full text-xs lg:text-sm"
              size="sm"
            >
              {changingPlan === plan.key
                ? "Changing..."
                : plan.key === currentPlan
                ? "Current Plan"
                : currentPlan !== "Free" && plan.key !== currentPlan
                ? "Change Plan"
                : "Select Plan"}
            </Button>

            {/* Show expiration date if subscription is cancelled, otherwise show cancel button */}
            {plan.key === currentPlan && (
              <>
                {subscriptionExpiresAt ? (
                  <div className="w-full text-xs text-gray-600 flex items-center justify-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                      Expires {formatExpirationDate(subscriptionExpiresAt)}
                    </span>
                  </div>
                ) : (
                  showCancelButton &&
                  onCancelSubscription && (
                    <button
                      onClick={onCancelSubscription}
                      className="w-full text-xs text-gray-500 hover:text-red-600 transition-colors duration-200 flex items-center justify-center space-x-1"
                    >
                      <X className="w-3 h-3" />
                      <span>Cancel subscription</span>
                    </button>
                  )
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
